// The reports as they were before the reporting release, restored from
// b17eb681 with only their imports changed and the provider functions develop
// deleted replaced by ./api, for builds with REACT_APP_DISABLE_NEW_REPORTS=true
// (see utils/features.js). One page at /reports?button=<report>, filtered through
// the tray list's own filters. Delete this folder, and the
// newReportsEnabled() checks that lead here, once production has the new
// reports.
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import {
  fetchOrganizationFilters,
  updateGeneralFilters,
  clearGeneralFilters,
  clearReportData,
  fetchAlgVectorTypes,
  fetchAlgVectorTypesGivenAlgVersion,
} from "../../../../actions";
import { useWebServer } from "../../../../providers";
import { useLegacyReportApi } from "./api";
import { loadReportData, REPORTS_TYPES } from "./actions";
import { phraseDownloadDataFilter } from "../../../../utils/dataDownload";
import { RAW_DATA_HEADER } from "./utils/headers";
import { withRegionDisplayNames } from "./utils/regionDisplayNames";
import { useLocation } from "react-router-dom";
import {
  handleDownloadChart,
  handleDownloadReport,
} from "./utils/download";
import { Reports } from "./Reports";

import {
  useGridApiRef,
  gridFilteredSortedRowIdsSelector,
} from "@mui/x-data-grid";

// useGridApiRef() hands back a ref object that only fills in once a DataGrid
// mounts against it, so `apiRef` alone is always truthy - chart reports render
// no grid at all and leave `current` null, which the selector dereferences.
const getFilteredRows = (apiRef) => {
  if (!apiRef?.current) {
    return null;
  }
  const filteredRowsIds = gridFilteredSortedRowIdsSelector(apiRef);
  return filteredRowsIds.map((id) => apiRef.current.getRow(id));
};

const EMPTY_REPORT = [];

const CLICKED_BUTTON_MAP = {
  "Raw-Data": REPORTS_TYPES.RAW_DATA,
  "Species-Summary-Among-Collections": REPORTS_TYPES.ABUNDANCE,
  "Weekly-Species-report": REPORTS_TYPES.WEEKLY_REPORT,
  "Pie-chart": REPORTS_TYPES.PIE_CHART,
  "Bar-chart": REPORTS_TYPES.ABUNDANCE,
  "LineGraph-chart": REPORTS_TYPES.LINE_CHART,
};

const Index = ({
  fetchOrganizationFilters,
  timeZone,
  generalFilters,
  loadReportData,
  clearReportData,
  fetchAlgVectorTypes,
  fetchAlgVectorTypesGivenAlgVersion,
  algVersions,
  data: reportData,
  loading,
}) => {
  const [isDropdownVisible, setIsDropdownVisible] = useState(true);

  const { sendRequest } = useWebServer();
  const {
    downloadOrganizationDataWithAbundance,
    downloadOrganizationDataWithWeekly,
    downloadSpeciesLineChart,
    downloadOrganizationDataWithRawData,
    getOrganizationSummaryData,
  } = useLegacyReportApi();
  const [showReport, setShowReport] = useState(false);
  const location = useLocation();
  // Derived rather than held in state: reading it straight off the router's
  // location keeps it in step with `isChart` on the very first render, and a
  // stale window.location read is what decided the download path before.
  const clickedButton = new URLSearchParams(location.search).get("button");
  const [readyDownload, setReadyDownload] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(
    RAW_DATA_HEADER.reduce((json, value, key) => {
      if (value?.["defaultHide"]) {
        json[value["field"]] = false;
      } else {
        json[value["field"]] = true;
      }
      return json;
    }, {})
  );

  const dataGridRef = useGridApiRef();

  useEffect(() => {
    return () => {
      clearReportData();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchOrganizationFilters(sendRequest);
  }, [fetchOrganizationFilters, sendRequest]);

  // The region names live in each alg version's manifest, so fetch the ones
  // the report's rows were identified under, plus the latest as a fallback.
  // Each version is only requested once, whether or not it comes back.
  const requestedAlgVersions = useRef(new Set());
  useEffect(() => {
    fetchAlgVectorTypes(sendRequest);
  }, [fetchAlgVectorTypes, sendRequest]);
  useEffect(() => {
    if (!Array.isArray(reportData)) {
      return;
    }
    reportData.forEach((row) => {
      const version = row?.algRegionName && row.algVersion;
      if (version && !requestedAlgVersions.current.has(version)) {
        requestedAlgVersions.current.add(version);
        fetchAlgVectorTypesGivenAlgVersion(sendRequest, version, {
          notifyOnError: false,
        });
      }
    });
  }, [reportData, fetchAlgVectorTypesGivenAlgVersion, sendRequest]);

  const data = useMemo(
    () => withRegionDisplayNames(reportData, algVersions),
    [reportData, algVersions]
  );

  useEffect(() => {
    if (data.length > 0) {
      setReadyDownload(true);
    }
  }, [data]);

  const onDownloadData = async (
    dataType = CLICKED_BUTTON_MAP[clickedButton]
  ) => {
    setIsDropdownVisible(!isDropdownVisible);
    const filters = phraseDownloadDataFilter(generalFilters, timeZone);
    loadReportData(
      downloadOrganizationDataWithRawData,
      downloadOrganizationDataWithWeekly,
      downloadOrganizationDataWithAbundance,
      downloadSpeciesLineChart,
      getOrganizationSummaryData,
      dataType,
      filters
    );
  };

  const isChart = Boolean(clickedButton?.toLowerCase().includes("chart"));

  const handleGenerateReport = () => {
    setShowReport(true);
    onDownloadData();
  };

  const handleDownloadClicked = () => {
    if (isChart) {
      handleDownloadChart(clickedButton);
      return;
    }

    const filteredData = getFilteredRows(dataGridRef);
    handleDownloadReport(
      filteredData ?? data,
      clickedButton,
      columnVisibilityModel
    );
  };

  return (
    <Reports
      timeZone={timeZone}
      loading={loading}
      isDropdownVisible={isDropdownVisible}
      setIsDropdownVisible={setIsDropdownVisible}
      handleGenerateReport={handleGenerateReport}
      isChart={isChart}
      clickedButton={clickedButton}
      readyDownload={readyDownload}
      handleDownloadClicked={handleDownloadClicked}
      rawData={data}
      onDownloadData={onDownloadData}
      columnVisibilityModel={columnVisibilityModel}
      setColumnVisibilityModel={setColumnVisibilityModel}
      showReport={showReport}
      dataGridRef={dataGridRef}
    />
  );
};

const mapStateToProps = (state) => {
  const { selectedDevice, ...devices } = state.devices;
  const { timeZone } = state.user;
  const curDevices = Object.values(
    _.pickBy(
      devices,
      (value, _) => value?.env === process.env.REACT_APP_ENV.toUpperCase()
    )
  );

  const defaultAlgorithmConfidenceThreshold =
    state.organization?.["defaultAlgorithmConfidenceThreshold"]?.["mosquito"];

  const generalFilters = {
    ...state.data.generalFilters,
    algThreshold: state.user.algConfidence
      ? state.user.algConfidence
      : defaultAlgorithmConfidenceThreshold,
  };

  return {
    devices: curDevices,
    selectedDevice,
    timeZone,
    generalFilters: generalFilters,
    data: state.report?.report?.data || EMPTY_REPORT,
    algVersions: state.algVersion,
    loading: state.report?.report?.loading || false,
  };
};

export default connect(mapStateToProps, {
  fetchOrganizationFilters,
  updateGeneralFilters,
  clearGeneralFilters,
  loadReportData,
  clearReportData,
  fetchAlgVectorTypes,
  fetchAlgVectorTypesGivenAlgVersion,
})(Index);
